import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { Badge } from "../components/Badge";
import { boardScrollbar } from "./boardScroll";
import { useSafeNavigate } from "../hooks/useSafeNavigate";
import { computeQuote, sendBlockers, type QuoteItem } from "../services/quoteMath";
import { QUOTES_PATH, quoteEditorPath } from "../services/quotePaths";
import { fetchProductCatalog, type CatalogProduct } from "../services/productCatalogService";
import {
  createQuote,
  duplicateQuote,
  fetchQuote,
  fetchQuoteDocumentUrl,
  generateQuotePdf,
  saveQuote,
  sendQuote,
  setQuoteStatus,
  type QuoteInput,
} from "../services/quotesService";
import type { Quote } from "../services/quotesAnalytics";

// ===========================================================================
// Armador de cotizaciones (#1545) — CRM → Negocios → Cotizaciones → una.
// Renglones con buscador del catálogo (CRM, HubSpot, licencias y Punto de
// venta), cantidad, precio, descuento por renglón y global, IVA y totales en
// vivo (quoteMath.ts; el servidor recalcula al guardar). Acciones: guardar,
// generar PDF, enviar por correo (PDF + liga pública), aceptar, rechazar con
// motivo, cancelar y duplicar. Una cotización con renglones «por cotizar» no
// se envía. Las que llegan de la tienda traen el distintivo «Tienda» y su folio.
// ===========================================================================

type TC = ReturnType<typeof buildThemeClasses>;

/** Un renglón mientras se edita: números como texto para no pelear con el input. */
interface ItemDraft {
  key: string;
  productId: string | null;
  source: string | null;
  sku: string | null;
  name: string;
  description: string;
  quantity: string;
  unitPrice: string;
  discountPct: string;
  /** IVA en porcentaje (16). */
  taxPct: string;
}

const LOCKED = new Set(["aceptada", "rechazada", "cancelada"]);
const STATUS_LABEL: Record<string, string> = {
  borrador: "Borrador",
  enviada: "Enviada",
  aprobada: "Aprobada",
  aceptada: "Aceptada",
  rechazada: "Rechazada",
  vencida: "Vencida",
  cancelada: "Cancelada",
};
const VIA_LABEL: Record<string, string> = { manual: "a mano", email: "por correo", public_link: "por el cliente (liga)", store: "desde la tienda", edit: "editada" };

let keySeq = 0;
const nextKey = () => `r${(keySeq += 1)}`;

const money = (value: number, currency = "MXN") =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);

const errText = (e: unknown, fallback: string) => {
  const data = (e as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data;
  return data?.meta?.message || data?.message || (e instanceof Error ? e.message : fallback);
};

const toInputDate = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fromInputDate = (value: string) => (value ? new Date(`${value}T23:59:59`).toISOString() : null);
const defaultExpiry = () => toInputDate(new Date(Date.now() + 30 * 86_400_000).toISOString());

const numOrNull = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function draftFrom(item: QuoteItem): ItemDraft {
  const rate = item.taxRate === null || item.taxRate === undefined ? 0.16 : Number(item.taxRate);
  return {
    key: nextKey(),
    productId: item.productId ?? null,
    source: item.source ?? null,
    sku: item.sku ?? null,
    name: item.name ?? "",
    description: item.description ?? "",
    quantity: String(item.quantity ?? 1),
    unitPrice: item.unitPrice === null || item.unitPrice === undefined ? "" : String(item.unitPrice),
    discountPct: String(item.discountPct ?? 0),
    taxPct: String(Math.round(rate * 10000) / 100),
  };
}

function itemFrom(d: ItemDraft): QuoteItem {
  return {
    productId: d.productId,
    source: d.source,
    sku: d.sku,
    name: d.name.trim(),
    description: d.description.trim() || null,
    quantity: Number(d.quantity) || 0,
    unitPrice: numOrNull(d.unitPrice),
    discountPct: Number(d.discountPct) || 0,
    taxRate: (numOrNull(d.taxPct) ?? 16) / 100,
  };
}

const blankItem = (): ItemDraft => ({
  key: nextKey(),
  productId: null,
  source: "manual",
  sku: null,
  name: "",
  description: "",
  quantity: "1",
  unitPrice: "",
  discountPct: "0",
  taxPct: "16",
});

function fromCatalog(p: CatalogProduct): ItemDraft {
  return {
    key: nextKey(),
    productId: p.id,
    source: p.origin,
    sku: p.sku,
    name: p.name ?? "Producto",
    description: p.description ?? "",
    quantity: "1",
    unitPrice: p.price === null || p.price === undefined ? "" : String(p.price),
    discountPct: "0",
    taxPct: "16",
  };
}

// --- catálogo -------------------------------------------------------------------

function CatalogPicker({ tc, fieldCls, product, onPick }: { tc: TC; fieldCls: string; product: string | null; onPick: (p: CatalogProduct) => void }) {
  const [rows, setRows] = useState<CatalogProduct[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [onlyProduct, setOnlyProduct] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchProductCatalog()
      .then((c) => alive && setRows(c.rows))
      .catch((e) => alive && setError(errText(e, "No se pudo cargar el catálogo")));
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows ?? [])
      .filter((p) => p.status !== "inactive")
      .filter((p) => !onlyProduct || !product || !p.platform || p.platform === product)
      .filter((p) => !q || [p.name, p.sku, p.description].some((v) => (v ?? "").toLowerCase().includes(q)))
      .slice(0, 30);
  }, [rows, query, onlyProduct, product]);

  return (
    <div className={`rounded-xl border p-3 ${tc.cardBorder} ${tc.cardBg}`} data-testid="quote-catalog">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar en el catálogo (nombre o SKU)"
          aria-label="Buscar en el catálogo"
          className={`min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm outline-none ${fieldCls}`}
        />
        {product && (
          <label className={`flex items-center gap-1 text-xs ${tc.textSecondary}`}>
            <input type="checkbox" checked={onlyProduct} onChange={(e) => setOnlyProduct(e.target.checked)} /> Solo {product}
          </label>
        )}
      </div>
      {error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : rows === null ? (
        <p className={`text-sm ${tc.textSecondary}`}>
          <i className="ri-loader-4-line mr-1 animate-spin" /> Cargando catálogo…
        </p>
      ) : filtered.length === 0 ? (
        <p className={`text-sm ${tc.textSecondary}`}>Sin resultados.</p>
      ) : (
        <ul className={`max-h-64 divide-y overflow-y-auto ${tc.border}`}>
          {filtered.map((p) => (
            <li key={p.key} className="flex items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className={`truncate text-sm font-medium ${tc.text}`}>{p.name}</p>
                <p className={`truncate text-xs ${tc.textSecondary}`}>
                  {[p.sku, p.platform, p.origin === "pos" ? "Punto de venta" : p.origin === "license" ? "Licencia" : p.origin === "hubspot" ? "HubSpot" : "CRM"]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className={`text-sm ${tc.text}`}>{p.price === null ? "Por cotizar" : money(p.price, p.currency || "MXN")}</span>
                <button onClick={() => onPick(p)} className="rounded-lg bg-primary-500 px-2 py-1 text-xs font-medium text-white hover:bg-primary-400" aria-label={`Agregar ${p.name}`}>
                  Agregar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- página -----------------------------------------------------------------------

export default function QuoteBuilder() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const fieldCls = isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900";
  const { quoteId = "nueva" } = useParams();
  const [search] = useSearchParams();
  const navigate = useSafeNavigate();
  const isNew = quoteId === "nueva";

  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [customer, setCustomer] = useState({ name: "", company: "", email: "", phone: "" });
  const [items, setItems] = useState<ItemDraft[]>([]);
  const [globalDiscount, setGlobalDiscount] = useState("0");
  const [validUntil, setValidUntil] = useState(defaultExpiry());
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");
  const [dirty, setDirty] = useState(isNew);
  const [showCatalog, setShowCatalog] = useState(false);

  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sendNote, setSendNote] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const hydrate = useCallback((q: Quote) => {
    setQuote(q);
    setTitle(q.title ?? "");
    setCustomer({
      name: q.customer?.name ?? q.contact ?? "",
      company: q.customer?.company ?? q.company ?? "",
      email: q.customer?.email ?? "",
      phone: q.customer?.phone ?? "",
    });
    setItems((q.items ?? []).map(draftFrom));
    setGlobalDiscount(String(q.globalDiscountPct ?? 0));
    setValidUntil(toInputDate(q.expiresAt));
    setNotes(q.notes ?? "");
    setTerms(q.terms ?? "");
    setDirty(false);
  }, []);

  useEffect(() => {
    if (isNew) return;
    let alive = true;
    setLoading(true);
    fetchQuote(quoteId)
      .then((q) => alive && hydrate(q))
      .catch((e) => alive && setLoadError(errText(e, "No se pudo abrir la cotización")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [quoteId, isNew, hydrate]);

  const status = quote?.effectiveStatus || quote?.status || "borrador";
  const mirror = Boolean(quote && String(quote.id).startsWith("hsdeal_"));
  const locked = mirror || LOCKED.has(String(quote?.status ?? ""));
  const currency = quote?.currency || "MXN";

  const asItems = useMemo(() => items.map(itemFrom), [items]);
  const totals = useMemo(() => computeQuote(asItems, Number(globalDiscount) || 0), [asItems, globalDiscount]);
  const blockers = useMemo(() => sendBlockers(asItems, fromInputDate(validUntil)), [asItems, validUntil]);

  const edit = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setDirty(true);
  };
  const patchItem = (key: string, patch: Partial<ItemDraft>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
    setDirty(true);
  };
  const removeItem = (key: string) => {
    setItems((prev) => prev.filter((it) => it.key !== key));
    setDirty(true);
  };
  const moveItem = (key: string, dir: -1 | 1) => {
    setItems((prev) => {
      const i = prev.findIndex((it) => it.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setDirty(true);
  };

  const payload = (): QuoteInput => ({
    ...(title.trim() ? { title: title.trim() } : {}),
    customer: {
      name: customer.name.trim() || null,
      company: customer.company.trim() || null,
      email: customer.email.trim() || null,
      phone: customer.phone.trim() || null,
    },
    ...(customer.company.trim() ? { company: customer.company.trim() } : {}),
    ...(customer.name.trim() ? { contact: customer.name.trim() } : {}),
    items: asItems,
    globalDiscountPct: Number(globalDiscount) || 0,
    expiresAt: fromInputDate(validUntil),
    notes: notes.trim() || null,
    terms: terms.trim() || null,
  });

  /** Valida lo que el servidor rechazaría igual, con un mensaje claro. */
  const localErrors = (): string | null => {
    if (asItems.some((it) => !it.name)) return "Cada renglón necesita nombre.";
    if (asItems.some((it) => !(it.quantity > 0))) return "Las cantidades deben ser mayores que 0.";
    if (asItems.some((it) => it.unitPrice !== null && it.unitPrice < 0)) return "Los precios no pueden ser negativos.";
    if (asItems.some((it) => (it.discountPct ?? 0) < 0 || (it.discountPct ?? 0) > 100)) return "El descuento va de 0 a 100 %.";
    const g = Number(globalDiscount);
    if (!Number.isFinite(g) || g < 0 || g > 100) return "El descuento global va de 0 a 100 %.";
    if (customer.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email.trim())) return "El correo del cliente no es válido.";
    return null;
  };

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setMessage(null);
    try {
      await fn();
    } catch (e) {
      setMessage({ ok: false, text: errText(e, "No se pudo completar la acción") });
    } finally {
      setBusy(null);
    }
  };

  /** Guarda (o crea) y devuelve la cotización tal como quedó en el servidor. */
  const persist = async (): Promise<Quote> => {
    const invalid = localErrors();
    if (invalid) throw new Error(invalid);
    if (isNew || !quote) {
      const created = await createQuote({
        ...payload(),
        dealId: search.get("dealId") || null,
        leadId: search.get("leadId") || null,
      });
      hydrate(created);
      navigate(quoteEditorPath(created.id));
      return created;
    }
    const saved = await saveQuote(quote.id, payload());
    hydrate(saved);
    return saved;
  };

  const save = () =>
    run("save", async () => {
      await persist();
      setMessage({ ok: true, text: "Cotización guardada" });
    });

  const openPdf = () =>
    run("pdf", async () => {
      const q = dirty ? await persist() : quote!;
      const { url, document } = await generateQuotePdf(q.id);
      setQuote((prev) => (prev ? { ...prev, document } : prev));
      window.open(url, "_blank", "noopener,noreferrer");
      setMessage({ ok: true, text: "PDF generado" });
    });

  const viewPdf = () =>
    run("view", async () => {
      window.open(await fetchQuoteDocumentUrl(quote!.id), "_blank", "noopener,noreferrer");
    });

  const send = () =>
    run("send", async () => {
      const q = dirty ? await persist() : quote!;
      const to = sendTo.trim();
      const out = await sendQuote(q.id, { ...(to ? { to } : {}), ...(sendNote.trim() ? { message: sendNote.trim() } : {}) });
      hydrate(out);
      setSendOpen(false);
      setMessage({ ok: true, text: `Cotización enviada a ${out.sentTo ?? to}` });
    });

  const changeStatus = (to: "aceptada" | "rechazada" | "cancelada", reason?: string) =>
    run(to, async () => {
      if (dirty && !LOCKED.has(String(quote?.status))) await persist();
      const out = await setQuoteStatus(quote!.id, to, reason);
      hydrate(out);
      setRejectOpen(false);
      setMessage({ ok: true, text: `Cotización ${STATUS_LABEL[to].toLowerCase()}` });
    });

  const duplicate = () =>
    run("duplicate", async () => {
      const copy = await duplicateQuote(quote!.id);
      navigate(quoteEditorPath(copy.id));
      hydrate(copy);
      setMessage({ ok: true, text: `Copia creada: ${copy.folio ?? copy.title}` });
    });

  const label = `mb-1 block text-xs ${tc.textSecondary}`;
  const input = `w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60 ${fieldCls}`;
  const cell = `w-full rounded-md border px-2 py-1 text-sm outline-none disabled:opacity-60 ${fieldCls}`;
  const btn = `flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50 ${fieldCls}`;
  const primary = "flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-50";
  const spin = (key: string, icon: string) => <i className={busy === key ? "ri-loader-4-line animate-spin" : icon} />;

  if (loading) {
    return (
      <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
        <i className="ri-loader-4-line mr-2 animate-spin" /> Cargando cotización…
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="p-10 text-center">
        <p className="text-sm text-red-500">{loadError}</p>
        <button onClick={() => navigate(QUOTES_PATH)} className="mt-3 text-sm text-primary-500 underline">
          Volver a Cotizaciones
        </button>
      </div>
    );
  }

  return (
    <div data-testid="quote-builder" className={`flex h-full min-h-0 w-full min-w-0 flex-col overflow-y-auto p-6 ${boardScrollbar(isDarkMode)}`}>
      {/* Encabezado */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(QUOTES_PATH)} title="Volver" aria-label="Volver a Cotizaciones" className={`rounded-lg border px-2 py-2 ${fieldCls}`}>
            <i className="ri-arrow-left-line" />
          </button>
          <div>
            <h1 className={`flex flex-wrap items-center gap-2 text-xl font-bold ${tc.text}`}>
              {isNew ? "Nueva cotización" : quote?.folio || quote?.title || "Cotización"}
              {!isNew && <Badge value={status} />}
              {quote?.source === "store" && (
                <span data-testid="quote-store-badge" className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-semibold text-sky-600">
                  <i className="ri-store-2-line" /> Tienda{quote.storeSlug ? ` · ${quote.storeSlug}` : ""}
                </span>
              )}
            </h1>
            <p className={`text-sm ${tc.textSecondary}`}>
              {quote?.deal ? `Negocio: ${quote.deal.title ?? quote.deal.id}${quote.deal.stage ? ` · ${quote.deal.stage}` : ""}` : isNew && search.get("dealId") ? "Se ligará al negocio y tomará su empresa, contacto y producto." : "Sin negocio ligado"}
              {quote?.product ? ` · ${quote.product}` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!locked && (
            <button onClick={save} disabled={!!busy || (!dirty && !isNew)} className={primary}>
              {spin("save", "ri-save-line")} {isNew ? "Crear cotización" : "Guardar"}
            </button>
          )}
          {!isNew && !mirror && (
            <button onClick={openPdf} disabled={!!busy} className={btn}>
              {spin("pdf", "ri-file-pdf-2-line")} Generar PDF
            </button>
          )}
          {!isNew && quote?.document && (
            <button onClick={viewPdf} disabled={!!busy} className={btn}>
              {spin("view", "ri-file-text-line")} Ver documento
            </button>
          )}
          {!isNew && !locked && (
            <button
              onClick={() => {
                setSendTo(customer.email || quote?.sentTo || "");
                setSendOpen((v) => !v);
              }}
              disabled={!!busy || blockers.length > 0}
              title={blockers.join(" ")}
              className={primary}
            >
              <i className="ri-send-plane-line" /> Enviar por correo
            </button>
          )}
          {!isNew && !mirror && (
            <button onClick={duplicate} disabled={!!busy} className={btn}>
              {spin("duplicate", "ri-file-copy-line")} Duplicar
            </button>
          )}
        </div>
      </div>

      {message && (
        <p role="status" className={`mb-3 text-sm ${message.ok ? "text-emerald-600" : "text-red-500"}`}>
          {message.text}
        </p>
      )}
      {mirror && (
        <p className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700">
          Esta cotización refleja un negocio de HubSpot: su importe y su estado los manda el negocio. Duplícala para armar una con renglones.
        </p>
      )}
      {!isNew && !locked && dirty && ["enviada", "vencida", "aprobada"].includes(status) && (
        <p data-testid="quote-edit-after-send" className="mb-3 rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 text-sm text-sky-700">
          El cliente ya tiene esta cotización. Si cambias renglones, vigencia o condiciones, al guardar se crea la versión {(quote?.revision ?? 1) + 1}: su
          liga mostrará la nueva, el PDF anterior se quita y tendrás que reenviarla.
        </p>
      )}
      {!isNew && !locked && blockers.length > 0 && (
        <div data-testid="quote-send-blockers" className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700">
          <strong>Para poder enviarla:</strong> {blockers.join(" ")}
        </div>
      )}

      {/* Enviar */}
      {sendOpen && !locked && (
        <div className={`mb-4 space-y-3 rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`} data-testid="quote-send-panel">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className={label} htmlFor="quote-send-to">
                Para (correo)
              </label>
              <input id="quote-send-to" type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="cliente@empresa.com" className={input} />
            </div>
            <div>
              <label className={label} htmlFor="quote-send-note">
                Mensaje (opcional)
              </label>
              <input id="quote-send-note" value={sendNote} onChange={(e) => setSendNote(e.target.value)} className={input} />
            </div>
          </div>
          <p className={`text-xs ${tc.textSecondary}`}>Se adjunta el PDF y una liga para que el cliente la vea y la acepte o rechace en línea. La cotización pasa a «Enviada».</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setSendOpen(false)} className={btn}>
              Cancelar
            </button>
            <button onClick={send} disabled={!!busy} className={primary}>
              {spin("send", "ri-send-plane-line")} Enviar
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* Columna principal: datos + renglones */}
        <div className="space-y-4 xl:col-span-2">
          <div className={`grid grid-cols-1 gap-3 rounded-xl border p-4 md:grid-cols-2 ${tc.cardBorder} ${tc.cardBg}`}>
            <div className="md:col-span-2">
              <label className={label} htmlFor="quote-title">
                Título
              </label>
              <input id="quote-title" value={title} disabled={locked} onChange={(e) => edit(setTitle)(e.target.value)} placeholder="Cotización — Empresa" className={input} />
            </div>
            <div>
              <label className={label} htmlFor="quote-customer-company">
                Empresa del cliente
              </label>
              <input id="quote-customer-company" value={customer.company} disabled={locked} onChange={(e) => edit(setCustomer)({ ...customer, company: e.target.value })} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="quote-customer-name">
                Contacto
              </label>
              <input id="quote-customer-name" value={customer.name} disabled={locked} onChange={(e) => edit(setCustomer)({ ...customer, name: e.target.value })} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="quote-customer-email">
                Correo del cliente
              </label>
              <input id="quote-customer-email" type="email" value={customer.email} disabled={locked} onChange={(e) => edit(setCustomer)({ ...customer, email: e.target.value })} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="quote-customer-phone">
                Teléfono
              </label>
              <input id="quote-customer-phone" value={customer.phone} disabled={locked} onChange={(e) => edit(setCustomer)({ ...customer, phone: e.target.value })} className={input} />
            </div>
          </div>

          <div className={`rounded-xl border ${tc.cardBorder} ${tc.cardBg}`}>
            <div className={`flex flex-wrap items-center justify-between gap-2 border-b p-3 ${tc.border}`}>
              <h2 className={`text-sm font-bold ${tc.text}`}>Renglones</h2>
              {!locked && (
                <div className="flex gap-2">
                  <button onClick={() => setShowCatalog((v) => !v)} className={btn}>
                    <i className="ri-search-line" /> Agregar del catálogo
                  </button>
                  <button
                    onClick={() => {
                      setItems((prev) => [...prev, blankItem()]);
                      setDirty(true);
                    }}
                    className={btn}
                  >
                    <i className="ri-add-line" /> Renglón libre
                  </button>
                </div>
              )}
            </div>
            {showCatalog && !locked && (
              <div className="p-3">
                <CatalogPicker
                  tc={tc}
                  fieldCls={fieldCls}
                  product={quote?.product ?? null}
                  onPick={(p) => {
                    setItems((prev) => [...prev, fromCatalog(p)]);
                    setDirty(true);
                  }}
                />
              </div>
            )}
            {items.length === 0 ? (
              <p className={`p-6 text-center text-sm ${tc.textSecondary}`}>Sin renglones. Agrega productos del catálogo o un renglón libre.</p>
            ) : (
              <div className={`overflow-x-auto ${boardScrollbar(isDarkMode)}`}>
                <table className="w-full min-w-[820px] text-sm">
                  <thead className={tc.textSecondary}>
                    <tr className="text-left text-xs">
                      <th className="p-2">Concepto</th>
                      <th className="w-20 p-2 text-right">Cant.</th>
                      <th className="w-32 p-2 text-right">Precio unit.</th>
                      <th className="w-20 p-2 text-right">Desc. %</th>
                      <th className="w-20 p-2 text-right">IVA %</th>
                      <th className="w-32 p-2 text-right">Importe</th>
                      <th className="w-24 p-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => {
                      const amount = totals.items[i]?.amount ?? 0;
                      const unpriced = it.unitPrice.trim() === "";
                      return (
                        <tr key={it.key} data-testid={`quote-item-${i}`} className={`border-t align-top ${tc.border}`}>
                          <td className="p-2">
                            <input aria-label={`Concepto ${i + 1}`} value={it.name} disabled={locked} onChange={(e) => patchItem(it.key, { name: e.target.value })} className={cell} />
                            <input
                              aria-label={`Descripción ${i + 1}`}
                              value={it.description}
                              disabled={locked}
                              onChange={(e) => patchItem(it.key, { description: e.target.value })}
                              placeholder="Descripción (opcional)"
                              className={`${cell} mt-1 text-xs`}
                            />
                            {(it.sku || it.source) && (
                              <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>{[it.sku, it.source === "store" ? "tienda" : it.source].filter(Boolean).join(" · ")}</p>
                            )}
                          </td>
                          <td className="p-2">
                            <input aria-label={`Cantidad ${i + 1}`} type="number" min="0" step="1" value={it.quantity} disabled={locked} onChange={(e) => patchItem(it.key, { quantity: e.target.value })} className={`${cell} text-right`} />
                          </td>
                          <td className="p-2">
                            <input
                              aria-label={`Precio ${i + 1}`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={it.unitPrice}
                              disabled={locked}
                              placeholder="Por cotizar"
                              onChange={(e) => patchItem(it.key, { unitPrice: e.target.value })}
                              className={`${cell} text-right ${unpriced ? "border-amber-500" : ""}`}
                            />
                          </td>
                          <td className="p-2">
                            <input aria-label={`Descuento ${i + 1}`} type="number" min="0" max="100" step="0.5" value={it.discountPct} disabled={locked} onChange={(e) => patchItem(it.key, { discountPct: e.target.value })} className={`${cell} text-right`} />
                          </td>
                          <td className="p-2">
                            <input aria-label={`IVA ${i + 1}`} type="number" min="0" max="100" step="1" value={it.taxPct} disabled={locked} onChange={(e) => patchItem(it.key, { taxPct: e.target.value })} className={`${cell} text-right`} />
                          </td>
                          <td className={`p-2 text-right font-medium ${tc.text}`} data-testid={`quote-item-amount-${i}`}>
                            {unpriced ? <span className="text-amber-600">Por cotizar</span> : money(amount, currency)}
                          </td>
                          <td className="p-2 text-right">
                            {!locked && (
                              <div className="flex justify-end gap-1">
                                <button onClick={() => moveItem(it.key, -1)} aria-label={`Subir renglón ${i + 1}`} className={`rounded p-1 ${tc.textSecondary}`}>
                                  <i className="ri-arrow-up-s-line" />
                                </button>
                                <button onClick={() => moveItem(it.key, 1)} aria-label={`Bajar renglón ${i + 1}`} className={`rounded p-1 ${tc.textSecondary}`}>
                                  <i className="ri-arrow-down-s-line" />
                                </button>
                                <button onClick={() => removeItem(it.key)} aria-label={`Quitar renglón ${i + 1}`} className="rounded p-1 text-red-500 hover:bg-red-500/10">
                                  <i className="ri-delete-bin-line" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className={`grid grid-cols-1 gap-3 rounded-xl border p-4 md:grid-cols-2 ${tc.cardBorder} ${tc.cardBg}`}>
            <div>
              <label className={label} htmlFor="quote-terms">
                Condiciones (las ve el cliente)
              </label>
              <textarea id="quote-terms" rows={3} value={terms} disabled={locked} onChange={(e) => edit(setTerms)(e.target.value)} placeholder="Forma de pago, tiempos de entrega, garantía…" className={input} />
            </div>
            <div>
              <label className={label} htmlFor="quote-notes">
                Notas internas
              </label>
              <textarea id="quote-notes" rows={3} value={notes} disabled={locked} onChange={(e) => edit(setNotes)(e.target.value)} className={input} />
            </div>
          </div>
        </div>

        {/* Columna lateral: totales, vigencia, estado */}
        <div className="space-y-4">
          <div className={`space-y-2 rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`} data-testid="quote-totals">
            <div className="flex items-center justify-between text-sm">
              <span className={tc.textSecondary}>Subtotal</span>
              <span className={tc.text} data-testid="quote-subtotal">
                {money(totals.subtotal, currency)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2 text-sm">
              <label htmlFor="quote-global-discount" className={tc.textSecondary}>
                Descuento global %
              </label>
              <input
                id="quote-global-discount"
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={globalDiscount}
                disabled={locked}
                onChange={(e) => edit(setGlobalDiscount)(e.target.value)}
                className={`w-20 rounded-md border px-2 py-1 text-right text-sm outline-none ${fieldCls}`}
              />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className={tc.textSecondary}>Descuentos</span>
              <span className={tc.text} data-testid="quote-discount">
                - {money(totals.discountTotal, currency)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className={tc.textSecondary}>IVA</span>
              <span className={tc.text} data-testid="quote-tax">
                {money(totals.taxTotal, currency)}
              </span>
            </div>
            <div className={`flex items-center justify-between border-t pt-2 text-base font-bold ${tc.border}`}>
              <span className={tc.text}>Total</span>
              <span className="text-primary-500" data-testid="quote-total">
                {money(totals.total, currency)}
              </span>
            </div>
            {totals.hasUnpriced && <p className="text-xs text-amber-600">Hay renglones por cotizar: no suman al total.</p>}
            <div className="pt-2">
              <label className={label} htmlFor="quote-valid-until">
                Válida hasta
              </label>
              <input id="quote-valid-until" type="date" value={validUntil} disabled={locked} onChange={(e) => edit(setValidUntil)(e.target.value)} className={input} />
            </div>
          </div>

          {!isNew && !mirror && (
            <div className={`space-y-2 rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <h3 className={`text-sm font-bold ${tc.text}`}>Respuesta del cliente</h3>
              {quote?.publicUrl && (
                <p className={`break-all text-xs ${tc.textSecondary}`}>
                  Liga pública:{" "}
                  <a href={quote.publicUrl} target="_blank" rel="noopener noreferrer" className="text-primary-500 underline">
                    {quote.publicUrl}
                  </a>
                </p>
              )}
              {quote?.acceptedAt && <p className="text-sm text-emerald-600">Aceptada el {new Date(quote.acceptedAt).toLocaleDateString("es-MX")} {quote.acceptedVia === "public_link" ? "por el cliente" : "a mano"}.</p>}
              {quote?.rejectedAt && (
                <p className="text-sm text-red-500">
                  Rechazada el {new Date(quote.rejectedAt).toLocaleDateString("es-MX")}
                  {quote.rejectReason ? `: ${quote.rejectReason}` : ""}.
                </p>
              )}
              {!locked && (
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => changeStatus("aceptada")} disabled={!!busy || status === "borrador"} title={status === "borrador" ? "Primero envíala" : ""} className={btn}>
                    {spin("aceptada", "ri-checkbox-circle-line")} Marcar aceptada
                  </button>
                  <button onClick={() => setRejectOpen((v) => !v)} disabled={!!busy || status === "borrador"} className={btn}>
                    <i className="ri-close-circle-line" /> Rechazada
                  </button>
                  <button onClick={() => changeStatus("cancelada")} disabled={!!busy} className={btn}>
                    {spin("cancelada", "ri-forbid-line")} Cancelar
                  </button>
                </div>
              )}
              {rejectOpen && !locked && (
                <div className="space-y-2" data-testid="quote-reject-panel">
                  <label className={label} htmlFor="quote-reject-reason">
                    Motivo (opcional)
                  </label>
                  <input id="quote-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className={input} />
                  <button onClick={() => changeStatus("rechazada", rejectReason.trim() || undefined)} disabled={!!busy} className={primary}>
                    {spin("rechazada", "ri-close-circle-line")} Confirmar rechazo
                  </button>
                </div>
              )}
            </div>
          )}

          {!isNew && (quote?.statusHistory?.length ?? 0) > 0 && (
            <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`} data-testid="quote-history">
              <h3 className={`mb-2 text-sm font-bold ${tc.text}`}>Historial</h3>
              <ol className="space-y-1">
                {[...(quote?.statusHistory ?? [])].reverse().map((h, i) => (
                  <li key={`${h.at}-${i}`} className={`text-xs ${tc.textSecondary}`}>
                    <span className={tc.text}>{STATUS_LABEL[h.to] ?? h.to}</span> · {new Date(h.at).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}
                    {h.via ? ` · ${VIA_LABEL[h.via] ?? h.via}` : ""}
                    {h.reason ? ` · «${h.reason}»` : ""}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
